import React from 'react';
import { Link, Navigate } from 'react-router-dom';
import LandingNav from '../components/LandingNav';
import Footer from '../components/Footer';
import Reveal from '../components/Reveal';
import { Capacitor } from '@capacitor/core';
import { useAuth } from '../context/AuthContext';
// 🔑 The step count in "How It Works" is the guided flow's own (Jesse, 2026-10-19; test/borrowerCopyTerms).
import { TOTAL_STEPS } from '../constants/guidedFlowSteps';
// 🔑 The service-area stat names the licensed states from the one list (test/noGeographicLiterals).
import { LICENSED_STATES, licensedStatesShort } from '../constants/marketExpansion';

// 🔑 The footnotes for the "*" claims on this page (Jesse, 2026-10-19), shown in the footer's disclaimer block.
export const LANDING_FOOTNOTES = ['*Some files require additional review by our team.'];

// ─── How It Works steps ───────────────────────────────────────────────────────
const STEPS = [
  {
  number: '01',
  title: 'Create Your Account',
  description:
  'Sign up in under 2 minutes. Doorly doesn’t pull your credit upfront — we verify your income, assets, and rental history first.',
  icon: (
  <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
  d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
  </svg>
  ),
  },
  {
  number: '02',
  title: 'Complete Your Application',
  description:
  `Fill out our guided ${TOTAL_STEPS}-step application and upload your documents digitally all in one place.`,
  icon: (
  <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
  d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
  </svg>
  ),
  },
  {
  number: '03',
  title: 'Instant Decision*',
  description:
  'Get approved based on your real financial profile\u2014not just traditional credit rules. Doorly reviews your application and determines your buying power.',
  icon: (
  <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
  d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
  ),
  },
  {
  number: '04',
  title: 'Shop for Homes',
  description:
  'Once approved, browse every available home within your budget\u2014powered by real-time MLS listings.',
  icon: (
  <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
  d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
  </svg>
  ),
  },
  {
  number: '05',
  title: 'Close on Your Home',
  description:
  "Review disclosures, sign digitally, and close \u2014 all coordinated through Doorly\u2019s proprietary same-day resale process.",
  icon: (
  <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
  d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
  </svg>
  ),
  },
];

// ─── Why Doorly benefits ──────────────────────────────────────────────────────
const BENEFITS = [
  {
  title: 'Instant Decisions',
  description:
  "Once your application is complete, you'll get an instant decision.*",
  icon: (
  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
  d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
  ),
  },
  {
  title: 'Flexible Approval',
  description:
  'We evaluate your full financial picture\u2014not just your credit score\u2014so you can get approved even when traditional lenders say no.',
  icon: (
  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
  d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
  </svg>
  ),
  },
  {
  title: '100% Digital Process',
  description:
  'Apply, upload documents, e-sign disclosures, and track your loan status all from your phone or computer.',
  icon: (
  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
  d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
  </svg>
  ),
  },
  {
  title: 'Bank-Level Security',
  description:
  '256-bit encryption protects every document and piece of personal information you share with us.',
  icon: (
  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
  d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
  </svg>
  ),
  },
  {
  title: 'Transparent Pricing',
  description:
  'See every number — purchase price, rate, closing costs, and monthly payment — before you commit.',
  icon: (
  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
  d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
  </svg>
  ),
  },
  {
  title: 'Built for Real Buyers',
  description:
  'Designed for gig workers, self-employed buyers, and anyone who doesn\u2019t fit the traditional lending box.',
  icon: (
  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
  d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
  </svg>
  ),
  },
];

// ─── What to Expect timeline ──────────────────────────────────────────────────
const TIMELINE = [
  {
  // 🔑 Steps 1–3 carry no day label (Jesse, 2026-10-19): the step number only.
  phase: null,
  title: 'Start Your Application',
  detail:
  'Create your account and work through each step at your own pace \u2014 save and come back anytime.',
  color: 'blue',
  },
  {
  phase: null,
  title: 'Verify Your Information',
  detail:
  'Upload your documents directly in the app to verify income and assets.',
  color: 'yellow',
  },
  {
  phase: null,
  title: 'Get Approved',
  detail:
  "Once your application is complete, you'll get an instant decision.*",
  color: 'green',
  },
  {
  phase: 'After Approval',
  title: 'Browse & Select Your Home',
  detail:
  'Browse all active listings in the app and choose the home that fits your budget. Doorly makes the offer and purchases the home, then sells it to you with Doorly financing.',
  color: 'tan',
  },
  {
  phase: 'Day 20-30',
  title: 'Closing & Move In',
  detail:
  'Doorly coordinates the purchase and financing. Review and sign everything digitally\u2014then get your keys and move in. You own the home from day one.',
  color: 'black',
  },
];

// Brand-only palette: #3a6ad9 (blue), #fffd5b (yellow), #acf44e (green),
// #111111 (near-black). Yellow/green dots use near-black numbers for
// readability since white-on-bright fails contrast. Badge backgrounds are
// brand-derived tints, never Tailwind defaults.
const timelineColors = {
  blue:   { dot: 'bg-[#3a6ad9] text-white',      badge: 'bg-[#eff4ff] text-[#3a6ad9]', border: 'border-[#bfcff8]' },
  tan:    { dot: 'bg-[#FFDD86] text-[#111111]',  badge: 'bg-[#fff7e0] text-[#111111]', border: 'border-[#FFDD86]' },
  yellow: { dot: 'bg-[#fffd5b] text-[#111111]',  badge: 'bg-[#fffde4] text-[#111111]', border: 'border-[#fffd5b]' },
  green:  { dot: 'bg-[#acf44e] text-[#111111]',  badge: 'bg-[#f0fde4] text-[#111111]', border: 'border-[#d4f9a0]' },
  black:  { dot: 'bg-[#111111] text-white',      badge: 'bg-[#f3f4f6] text-[#111111]', border: 'border-[#e5e7eb]' },
};

// ─── Page ─────────────────────────────────────────────────────────────────────
function LandingPage() {
  // Auth-aware entry. The app cold-starts at "/" (manifest start_url) on web, PWA,
  // and native — so a returning user with a durable, already-restored session would
  // otherwise land on the signed-out marketing page over a live session. If a session
  // was restored, forward into the app. Gate strictly on authLoading so we never
  // redirect before the restore resolves (no flash, no premature bounce); a genuinely
  // logged-out visitor (and JS crawlers) fall through and render the page normally.
  const { user, authLoading } = useAuth();
  if (authLoading) return null;
  if (user) return <Navigate to="/dashboard" replace />;
  // 🔑 NATIVE-ONLY ENTRY (Jesse, 2026-10-24): the app has no marketing page — a logged-out launch opens sign-in.
  // Web (and crawlers) unchanged. Reaches 1.0.2 binaries by OTA too; it needs no native plugin.
  if (Capacitor.isNativePlatform()) return <Navigate to="/login" replace />;

  return (
  <div className="min-h-screen flex flex-col bg-white">
  <LandingNav />

  {/* ── Hero ───────────────────────────────────────────────────────────── */}
  <section
  className="relative isolate overflow-hidden"
  style={{ background: 'linear-gradient(180deg, rgba(58,106,217,0.06) 0%, #FFFFFF 65%)' }}
  >
  {/* Full-bleed crossed-line grid (96px tiles), radial-masked so it fades
      out toward the bottom of the hero. */}
  <div
  aria-hidden="true"
  className="pointer-events-none absolute inset-0"
  style={{
  backgroundImage:
  'linear-gradient(rgba(0,0,0,0.06) 1px, transparent 1px), linear-gradient(90deg, rgba(0,0,0,0.06) 1px, transparent 1px)',
  backgroundSize: '96px 96px',
  WebkitMaskImage: 'radial-gradient(ellipse at 50% 0%, #000 25%, transparent 85%)',
  maskImage: 'radial-gradient(ellipse at 50% 0%, #000 25%, transparent 85%)',
  }}
  />

  <div
  className="relative mx-auto flex flex-col items-center text-center px-5 pt-14 pb-12 md:px-8 md:pt-20 md:pb-16"
  style={{ maxWidth: '1080px', gap: '30px' }}
  >
  {/* Welcome pill */}
  <span
  className="inline-flex items-center gap-2 bg-white"
  style={{ border: '1px solid rgba(0,0,0,0.1)', borderRadius: '9999px', padding: '4px 5px 4px 16px', fontSize: '14px', fontWeight: 500, color: '#000000' }}
  >
  Welcome to
  <span
  style={{ backgroundColor: '#fffd5b', borderRadius: '9999px', padding: '4px 12px', fontWeight: 500, fontSize: '13.5px', border: '1px solid rgba(0,0,0,0.08)' }}
  >
  Doorly
  </span>
  </span>

  {/* Headline — single h1, two color-split spans for SEO / SR flow */}
  <h1
  className="hero-headline"
  style={{
  fontFamily: 'var(--font-display)',
  fontWeight: 600,
  letterSpacing: '-0.035em',
  lineHeight: 0.98,
  margin: 0,
  }}
  >
  <span style={{ display: 'block', color: '#000000' }}>Homeownership,</span>
  <span style={{ display: 'block', color: 'var(--color-brand-blue)' }}>Unlocked.</span>
  </h1>

  {/* Subhead */}
  <p
  style={{ fontSize: '16px', lineHeight: 1.5, color: 'rgba(0,0,0,0.6)', maxWidth: '52ch', textWrap: 'pretty', margin: 0 }}
  >
  From application to keys, all in one place. Doorly helps hardworking buyers get approved and move into a home&mdash;even when traditional lenders can&apos;t.
  </p>

  {/* CTA row */}
  <div className="flex flex-col sm:flex-row items-center" style={{ gap: '20px' }}>
  <Link to="/pre-qualify" className="doorly-cta doorly-cta--lg">
  <span className="doorly-cta-text">Get Started</span>
  <span className="doorly-cta-badge" aria-hidden="true">
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
  <path d="M5 12h14M13 6l6 6-6 6" />
  </svg>
  </span>
  </Link>
  <Link
  to="/login"
  className="inline-flex items-center gap-1"
  style={{ fontSize: '15px', fontWeight: 500, color: '#000000', textDecoration: 'none' }}
  >
  Already started? Sign in
  <span aria-hidden="true" style={{ opacity: 0.5 }}>&rarr;</span>
  </Link>
  </div>

  {/* Reassurance row — green dot + pipe-separated items */}
  <div style={{
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  fontSize: 13,
  color: 'rgba(0,0,0,0.5)',
  flexWrap: 'wrap',
  justifyContent: 'center',
  margin: 0,
  }}>
  {/* NOT "soft-credit only" — the ReadyCheck inquiry is HARD. That copy predates
      the 2026-06 soft->hard reframe and was left behind when the flow changed.
      This says what is still true and still favourable: nothing touches credit
      until ReadyCheck, and pre-qualification is self-reported. */}
  <span>No credit check to start</span>
  <span style={{ color: '#acf44e', fontWeight: 500 }}>|</span>
  <span>Instant decisions*</span>
  <span style={{ color: '#acf44e', fontWeight: 500 }}>|</span>
  <span>Fast closings</span>
  </div>
  </div>
  </section>

  {/* ── Trust Stats Bar ────────────────────────────────────────────────── */}
  <section className="bg-gray-50 border-b border-gray-100 py-10 px-4 sm:px-6 lg:px-8">
  <div className="max-w-5xl mx-auto grid grid-cols-1 sm:grid-cols-3 gap-6">
  {[
  // 🔑 No closed file to measure a time to close against (census time-to-close, 2026-10-19: 0), so no day count.
  { value: '100% Digital', label: 'From application to closing' },
  { value: licensedStatesShort(), label: LICENSED_STATES.length > 1 ? 'Licensed Service Areas' : 'Licensed Service Area' },
  { value: 'Flexible', label: 'Underwriting for Modern Buyers' },
  ].map((stat, i) => (
  <Reveal key={stat.label} delay={i * 80} y={10}>
  <div className="text-center">
  <p className="text-2xl sm:text-3xl font-extrabold text-black">{stat.value}</p>
  <p className="text-sm text-gray-500 mt-0.5">{stat.label}</p>
  </div>
  </Reveal>
  ))}
  </div>
  </section>

  {/* ── How It Works ──────────────────────────────────────────────────── */}
  <section className="bg-white py-20 px-4 sm:px-6 lg:px-8" id="how-it-works">
  <div className="max-w-7xl mx-auto">
  <Reveal>
  <div className="text-center mb-14">
  <p className="text-doorly-blue text-sm font-semibold uppercase tracking-wider mb-2">How it Works</p>
  <h2 className="text-3xl sm:text-4xl font-bold text-black mb-4">
  This isn&apos;t seller financing. It&apos;s <span style={{ color: '#3a6ad9' }}>Doorly financing.</span>
  </h2>
  <p className="text-gray-600 text-lg max-w-2xl mx-auto">
  Five steps from account creation to getting your keys. We handle the complexity you focus on finding your home.
  </p>
  </div>
  </Reveal>

  {/* Steps grid */}
  <div className="grid grid-cols-1 md:grid-cols-5 gap-6">
  {STEPS.map((step, i) => (
  <Reveal key={step.number} delay={i * 80} y={10}>
  <div className="relative flex flex-col h-full">
  {/* Connector line (desktop only, between cards) */}
  {i < STEPS.length - 1 && (
  <div className="hidden md:block absolute top-9 left-full w-full h-px bg-gray-200 z-0" style={{ width: 'calc(100% - 72px)', left: '72px' }} />
  )}
  <div className="relative z-10 flex flex-col items-center text-center bg-white rounded-2xl border border-gray-100 shadow-sm p-6 h-full hover:shadow-md transition-shadow">
  {/* Number badge */}
  <div
  className="w-14 h-14 rounded-2xl flex items-center justify-center text-white mb-4 flex-shrink-0"
  style={{ backgroundColor: '#3a6ad9' }}
  >
  {step.icon}
  </div>
  <span className="text-xs font-bold text-gray-300 uppercase tracking-widest mb-1">{step.number}</span>
  <h3 className="text-base font-bold text-black mb-2">{step.title}</h3>
  <p className="text-gray-500 text-sm leading-relaxed">{step.description}</p>
  </div>
  </div>
  </Reveal>
  ))}
  </div>

  <Reveal delay={STEPS.length * 80}>
  <div className="text-center mt-10">
  <Link to="/pre-qualify" className="doorly-cta inline-flex">
  <span className="doorly-cta-text">Check My Eligibility</span>
  <span className="doorly-cta-badge" aria-hidden="true">→</span>
  </Link>
  </div>
  </Reveal>
  </div>
  </section>

  {/* ── Why Doorly ────────────────────────────────────────────────────── */}
  <section className="bg-gray-50 py-20 px-4 sm:px-6 lg:px-8 border-t border-gray-100">
  <div className="max-w-7xl mx-auto">
  <Reveal>
  <div className="text-center mb-14">
  <p className="text-doorly-blue text-sm font-semibold uppercase tracking-wider mb-2">Why Choose Us</p>
  <h2 className="text-3xl sm:text-4xl font-bold text-black mb-4">Why Doorly</h2>
  <p className="text-gray-600 text-lg max-w-2xl mx-auto">
  We built Doorly to make the mortgage process feel less like a bureaucratic nightmare and more like buying something online.
  </p>
  </div>
  </Reveal>

  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
  {BENEFITS.map((b, i) => (
  <Reveal key={b.title} delay={i * 60} y={10}>
  <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 hover:shadow-md transition-shadow group h-full">
  <div
  className="w-11 h-11 rounded-xl flex items-center justify-center mb-4 text-doorly-blue group-hover:bg-doorly-blue group-hover:text-white transition-colors"
  style={{ backgroundColor: '#eff3fe' }}
  >
  {b.icon}
  </div>
  <h3 className="text-base font-bold text-black mb-2">{b.title}</h3>
  <p className="text-gray-500 text-sm leading-relaxed">{b.description}</p>
  </div>
  </Reveal>
  ))}
  </div>
  </div>
  </section>

  {/* ── What to Expect ────────────────────────────────────────────────── */}
  <section className="bg-white py-20 px-4 sm:px-6 lg:px-8 border-t border-gray-100">
  <div className="max-w-4xl mx-auto">
  <Reveal>
  <div className="text-center mb-14">
  <p className="text-doorly-blue text-sm font-semibold uppercase tracking-wider mb-2">After You Sign Up</p>
  <h2 className="text-3xl sm:text-4xl font-bold text-black mb-4">What to Expect</h2>
  <p className="text-gray-600 text-lg max-w-2xl mx-auto">
  Here's exactly what happens after you create your Doorly account.
  </p>
  </div>
  </Reveal>

  {/* Vertical timeline */}
  <div className="relative">
  {/* Vertical line */}
  <div className="absolute left-6 top-0 bottom-0 w-px bg-gray-200 hidden sm:block" />

  <div className="space-y-8">
  {TIMELINE.map((item, i) => {
  const c = timelineColors[item.color];
  return (
  <Reveal key={item.title} delay={i * 80} y={10}>
  <div className="relative flex gap-6 sm:gap-8">
  {/* Timeline dot */}
  <div className={`hidden sm:flex w-12 h-12 rounded-full ${c.dot} items-center justify-center flex-shrink-0 z-10 font-bold text-sm`}>
  {i + 1}
  </div>

  {/* Card */}
  <div className={`flex-1 bg-white rounded-2xl border ${c.border} shadow-sm p-5 sm:p-6`}>
  <div className="flex flex-col sm:flex-row sm:items-center gap-2 mb-2">
  {item.phase ? (
  <span className={`inline-flex text-xs font-semibold px-2.5 py-1 rounded-full self-start ${c.badge}`}>
  {item.phase}
  </span>
  ) : (
  // No day label: the number dot carries the step on desktop; on a phone (dot hidden) the badge does.
  <span className={`sm:hidden inline-flex text-xs font-semibold px-2.5 py-1 rounded-full self-start ${c.badge}`}>
  Step {i + 1}
  </span>
  )}
  <h3 className="text-base font-bold text-black">{item.title}</h3>
  </div>
  <p className="text-gray-500 text-sm leading-relaxed">{item.detail}</p>
  </div>
  </div>
  </Reveal>
  );
  })}
  </div>
  </div>
  </div>
  </section>

  {/* ── Final CTA ─────────────────────────────────────────────────────── */}
  <section className="py-20 px-4 sm:px-6 lg:px-8 border-t border-gray-100" style={{ background: '#3a6ad9' }}>
  <div className="max-w-3xl mx-auto text-center">
  <Reveal>
  <div className="inline-flex items-center gap-2 bg-white/10 border border-white/20 text-white text-xs font-semibold px-3 py-1.5 rounded-full mb-6 uppercase tracking-wider">
  Get Started Today
  </div>
  </Reveal>
  <Reveal delay={80}>
  <h2 className="text-3xl sm:text-4xl font-bold text-white mb-5 leading-tight">
  Ready to Buy Your Home?
  </h2>
  </Reveal>
  <Reveal delay={160}>
  <p className="text-blue-100 text-lg mb-10 max-w-xl mx-auto leading-relaxed">
  Join the homebuyers who chose Doorly for a faster, simpler, and more transparent mortgage experience. Apply in minutes.
  </p>
  </Reveal>

  <Reveal delay={240}>
  <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
  <Link
  to="/pre-qualify"
  className="btn-primary text-[#3a6ad9] bg-white hover:bg-gray-50 transition-colors w-full sm:w-auto shadow-lg"
  >
  <span>Check My Eligibility</span>
  <span className="btn-arrow" style={{ backgroundColor: '#3a6ad9' }}>
  <svg viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M9 5l7 7-7 7"/></svg>
  </span>
  </Link>
  <Link
  to="/login"
  className="btn-secondary text-white border border-white/30 hover:bg-white/10 transition-colors w-full sm:w-auto"
  >
  <span>Already have an account? Log In</span>
  <span className="btn-arrow" style={{ backgroundColor: 'rgba(255,255,255,0.2)', border: '1px solid rgba(255,255,255,0.3)' }}>
  <svg viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M9 5l7 7-7 7"/></svg>
  </span>
  </Link>
  </div>
  </Reveal>

  {/* Bottom trust signals */}
  <Reveal delay={320}>
  <div className="flex flex-wrap justify-center gap-x-6 gap-y-2 mt-8 text-blue-200 text-xs">
  <span>NMLS #2764749</span>
  <span>Equal Housing Opportunity</span>
  <span>256-bit SSL Encryption</span>
  {/* Was "No Hard Credit Pull" — false, and worse for sitting in a COMPLIANCE
      row beside the NMLS number and Equal Housing Opportunity, where it reads as a
      regulatory assurance rather than marketing. Doorly runs exactly one hard
      inquiry per application with no re-pulls (UWGuideContent.jsx), which is a
      genuine advantage and can be stated without claiming an absence. */}
  <span>Single credit inquiry</span>
  </div>
  </Reveal>
  </div>
  </section>

  {/* 🔑 Every "*" on this page has its footnote in the footer's disclaimer block (Jesse, 2026-10-19;
      test/borrowerCopyTerms). The block is never hidden at any screen size. */}
  <Footer footnotes={LANDING_FOOTNOTES} />
  </div>
  );
}

export default LandingPage;
